import React from 'react'
import { cn } from 'components/UI/cn'
import { formatInteger } from 'utils/formatting'
import { UnifiedSearchTable } from './UnifiedSearchTable'
import { UnifiedSearchVariantTable } from './UnifiedSearchVariantTable'
import { UnifiedSearchEmptyState } from './UnifiedSearchEmptyState'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { VariantSearchResult } from 'services/search'
import { UnifiedSearchTab } from './constants'
import { SearchResultUnion } from './hooks/useUnifiedSearchAPI'

// Type for non-variant search results (used by UnifiedSearchTable)
type NonVariantSearchResult = ContactCompanySearchResult | DoorSearchResult

// Format count with "+" indicator if at the 10,000 cap
const formatCountWithCap = (count: number): string => {
  if (count === 10000) {
    return '10,000+'
  }
  return formatInteger(count)
}

interface UnifiedSearchResultsProps {
  selectedTab: UnifiedSearchTab
  setSelectedTab: (tab: UnifiedSearchTab) => void
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  variantCount: number
  myCompaniesCount: number
  myCompaniesResults: ContactCompanySearchResult[]
  myCompaniesLoading: boolean
  variantResults: VariantSearchResult[]
  currentResults: SearchResultUnion[] // Now properly typed instead of any[]
  debouncedSearchTerm: string
  isLoading: boolean
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onRowClick: (row: SearchResultUnion) => void
  selectedItem?: SearchResultUnion | null
  showRadioButtons?: boolean
  onVariantDataChange?: () => void
  /** When provided, only tabs whose key is in this array are shown. */
  visibleTabs?: UnifiedSearchTab[]
  /** Optional content rendered to the right of the tab strip on the same row. */
  filtersSlot?: React.ReactNode
}

export function UnifiedSearchResults(props: UnifiedSearchResultsProps) {
  const {
    selectedTab,
    setSelectedTab,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    variantCount,
    myCompaniesCount,
    myCompaniesResults,
    myCompaniesLoading,
    variantResults,
    currentResults,
    debouncedSearchTerm,
    isLoading,
    currentTotalRows,
    pageSize,
    currentPage,
    setCurrentPage,
    onRowClick,
    selectedItem,
    showRadioButtons = false,
    onVariantDataChange,
    visibleTabs,
    filtersSlot,
  } = props
  const handleTabChange = (tab: string) => {
    setSelectedTab(tab as UnifiedSearchTab)
  }

  // Check for results based on current tab
  const hasResults =
    selectedTab === 'variants'
      ? variantResults.length > 0
      : currentResults.length > 0
  const shouldShowTable = debouncedSearchTerm && (hasResults || isLoading)
  const shouldShowEmptyState =
    !debouncedSearchTerm || (debouncedSearchTerm && !isLoading && !hasResults)

  return (
    <div className="flex-1">
      {/* Tabs - hide entirely when only one tab is visible (single-purpose picker mode) */}
      <div className={cn('mb-4', visibleTabs?.length === 1 && 'hidden')}>
        <div className="flex h-12 w-full items-center justify-start gap-1 rounded-lg bg-surface-alt p-1">
          {(
            [
              // Why: 'mycompanies' only renders for callers that explicitly
              // request it. Other modals that mount UnifiedSearchResults
              // without visibleTabs (UnifiedSearchModal, document modals)
              // would otherwise leak user-scoped data into flows it wasn't
              // designed for.
              ...(visibleTabs?.includes('mycompanies')
                ? [
                    {
                      key: 'mycompanies' as const,
                      title: `My Companies (${formatCountWithCap(myCompaniesCount)})`,
                    },
                  ]
                : []),
              {
                key: 'firstbite',
                title: `FB Companies (${formatCountWithCap(firstBiteCount)})`,
              },
              {
                key: 'firstbitedoors',
                title: `FB Locations (${formatCountWithCap(firstBiteDoorCount)})`,
              },
              {
                key: 'unmatched',
                title: `Custom Companies (${formatCountWithCap(unmatchedCount)})`,
              },
              {
                key: 'unmatcheddoors',
                title: `Custom Locations (${formatCountWithCap(unmatchedDoorCount)})`,
              },
              {
                key: 'variants',
                title: `Product Variants (${formatCountWithCap(variantCount)})`,
              },
            ] as { key: UnifiedSearchTab; title: string }[]
          )
            .filter((tab) => !visibleTabs || visibleTabs.includes(tab.key))
            .map(({ key, title }) => {
              const isActive = selectedTab === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key)}
                  className={cn(
                    'flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-md px-3 py-2.5 text-base font-medium leading-6 tracking-tight transition-colors',
                    isActive
                      ? 'bg-surface text-fg shadow-sm'
                      : 'text-fg-secondary hover:text-fg'
                  )}
                >
                  {title}
                </button>
              )
            })}
        </div>
      </div>

      {/* Filters row (rendered below tabs) */}
      {filtersSlot && (
        <div className={cn('mb-4', visibleTabs?.length === 1 && 'hidden')}>
          {filtersSlot}
        </div>
      )}

      {/* Results */}
      <div className={`overflow-hidden flex flex-col h-[510px]`}>
        {/* My Companies — user's custom companies, no search required.
            selectedTab is forced to "unmatched" because UnifiedSearchTable
            drives row-click URL generation off that key and we want the
            same custom-company route handling here; the visible/active
            tab key in the modal remains "mycompanies". */}
        {selectedTab === 'mycompanies' && (
          <UnifiedSearchTable
            data={myCompaniesResults}
            isLoading={myCompaniesLoading}
            selectedTab="unmatched"
            currentTotalRows={myCompaniesResults.length}
            pageSize={pageSize}
            currentPage={1}
            setCurrentPage={() => {}}
            onRowClick={onRowClick as (row: NonVariantSearchResult) => void}
            selectedItem={selectedItem as NonVariantSearchResult | null}
            showRadioButtons={showRadioButtons}
          />
        )}

        {/* Show empty state only when not loading and no results */}
        {selectedTab !== 'mycompanies' && shouldShowEmptyState && (
          <UnifiedSearchEmptyState
            debouncedSearchTerm={debouncedSearchTerm}
            isLoading={isLoading}
            selectedTab={selectedTab}
            hasResults={hasResults}
          />
        )}

        {/* Show table when we have a search term and either have results or are loading */}
        {selectedTab !== 'mycompanies' &&
          shouldShowTable &&
          (selectedTab === 'variants' ? (
            <UnifiedSearchVariantTable
              data={variantResults}
              isLoading={isLoading}
              currentTotalRows={currentTotalRows}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onDataChange={onVariantDataChange}
            />
          ) : (
            <UnifiedSearchTable
              data={currentResults as NonVariantSearchResult[]}
              isLoading={isLoading}
              selectedTab={
                selectedTab as
                  | 'firstbite'
                  | 'unmatched'
                  | 'firstbitedoors'
                  | 'unmatcheddoors'
              }
              currentTotalRows={currentTotalRows}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              onRowClick={onRowClick as (row: NonVariantSearchResult) => void}
              selectedItem={selectedItem as NonVariantSearchResult | null}
              showRadioButtons={showRadioButtons}
            />
          ))}
      </div>
    </div>
  )
}
